import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import {
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Kanban,
  MoreVertical,
  Plus,
  Rows3,
  Search,
  Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { cn } from "@/lib/utils";
import {
  useDealsViewStore,
  type SortField,
} from "@/store/deals-view-store";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { AddDealDialog } from "./add-deal";
import { FilterSheet, FilterValues } from "./filter-sheet";

interface ToolbarProps {
  onSearch?: (query: string) => void;
  onFiltersChange: (filters: FilterValues) => void;
  deals: Record<string, DealTransformed>;
  dealCountByStatus: Record<string, number>;
}

const SORT_FIELDS: { value: SortField; label: string }[] = [
  { value: "lastActivity", label: "Last Activity" },
  { value: "name", label: "Deal Name" },
  { value: "fitScore", label: "Fit Score" },
  { value: "added", label: "Added Date" },
  { value: "status", label: "Status" },
];

// Shared chrome for every toolbar control. Single height, single border
// treatment, single transition — gives the action bar a unified rhythm
// instead of the mixed-button-style feel.
const CONTROL =
  "h-8 inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs text-foreground transition-colors hover:bg-foreground/[0.04]";

export const Toolbar = ({
  onSearch,
  onFiltersChange,
  deals,
  dealCountByStatus,
}: ToolbarProps) => {
  const [addDealOpen, setAddDealOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPad|iPhone|iPod/.test(navigator.platform);
  const { navigate } = useWorkspaceNavigation();
  const { view, sort, setView, setSort, toggleSortDirection } =
    useDealsViewStore();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const currentSortLabel =
    SORT_FIELDS.find((f) => f.value === sort.field)?.label ?? "Last Activity";

  const handleSortFieldClick = (field: SortField) => {
    if (field === sort.field) {
      toggleSortDirection();
    } else {
      // New field: default direction is desc for everything except name (asc).
      setSort({
        field,
        direction: field === "name" ? "asc" : "desc",
      });
    }
  };

  return (
    <div className="shrink-0">
      {/* Header — display title + primary actions */}
      <div className="flex h-16 items-end justify-between border-b border-border px-6 pb-3">
        <h1 className="font-serif text-[32px] leading-[1.15] tracking-tight text-foreground">
          Pipeline
        </h1>
        <div className="flex items-center gap-2">
          {/* Bulk add — secondary ghost. Less-frequent batch import action. */}
          <button
            type="button"
            onClick={() => navigate("/deals/bulk-add")}
            className="hidden md:inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          >
            <Upload className="h-3.5 w-3.5" />
            Bulk add
          </button>
          {/* Add deal — primary. The most-used action of an analyst's day,
              gets brand presence (--primary maps to DESIGN.md --ink). */}
          <button
            type="button"
            onClick={() => setAddDealOpen(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" />
            Add deal
          </button>
        </div>
      </div>

      {/* Toolbar — unified control row */}
      <div className="flex items-center gap-2 px-6 py-2.5">
        {/* Search occupies remaining width — extended so it feels like a
            workspace surface, not a constrained form field. Matches CONTROL
            chrome exactly (h-8, border-border, no shadow). */}
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Search deals by name, source, or industry"
            className="h-8 w-full rounded-md border-border bg-background pl-9 pr-3 text-xs placeholder:text-muted-foreground/70 focus-visible:ring-1 focus-visible:ring-foreground/20 focus-visible:ring-offset-0"
            onChange={(e) => onSearch?.(e.target.value)}
          />
          <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/70 lg:inline-flex">
            <span>{isMac ? "⌘" : "Ctrl"}</span>
            <span>K</span>
          </kbd>
        </div>

        <div className="flex shrink-0 items-center gap-2 ml-auto">
          {/* Sort By — matches CONTROL chrome */}
          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={`Sort by ${currentSortLabel}, ${sort.direction}`}
              className={cn(CONTROL, "hidden md:inline-flex")}
            >
              <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">Sort</span>
              <span>{currentSortLabel}</span>
              {sort.direction === "asc" ? (
                <ArrowUp className="h-3 w-3 text-muted-foreground" />
              ) : (
                <ArrowDown className="h-3 w-3 text-muted-foreground" />
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {SORT_FIELDS.map((f) => {
                const isActive = sort.field === f.value;
                return (
                  <DropdownMenuItem
                    key={f.value}
                    onClick={() => handleSortFieldClick(f.value)}
                    className="justify-between"
                  >
                    <span>{f.label}</span>
                    {isActive ? (
                      sort.direction === "asc" ? (
                        <ArrowUp className="h-3.5 w-3.5" />
                      ) : (
                        <ArrowDown className="h-3.5 w-3.5" />
                      )
                    ) : (
                      <Check className="h-3.5 w-3.5 opacity-0" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <FilterSheet
            onFiltersChange={onFiltersChange}
            deals={deals}
            dealCountByStatus={dealCountByStatus}
          />

          {/* Archived — opens the archived-deals view (back-arrow returns here) */}
          <button
            type="button"
            onClick={() => navigate("/archive")}
            aria-label="View archived deals"
            className={cn(CONTROL, "hidden md:inline-flex")}
          >
            <Archive className="h-3.5 w-3.5 text-muted-foreground" />
            <span>Archived</span>
          </button>

          {/* View switcher — matches CONTROL height + border */}
          <div
            role="group"
            aria-label="View mode"
            className="hidden md:inline-flex h-8 items-center rounded-md border border-border bg-background p-0.5"
          >
            <button
              type="button"
              aria-pressed={view === "table"}
              aria-label="Table view"
              onClick={() => setView("table")}
              className={cn(
                "inline-flex h-full items-center gap-1.5 rounded-[5px] px-2 text-xs transition-colors",
                view === "table"
                  ? "bg-foreground/[0.06] text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Rows3 className="h-3.5 w-3.5" />
              Table
            </button>
            <button
              type="button"
              aria-pressed={view === "kanban"}
              aria-label="Kanban view"
              onClick={() => setView("kanban")}
              className={cn(
                "inline-flex h-full items-center gap-1.5 rounded-[5px] px-2 text-xs transition-colors",
                view === "kanban"
                  ? "bg-foreground/[0.06] text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Kanban className="h-3.5 w-3.5" />
              Kanban
            </button>
          </div>

          {/* Mobile fallback */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="md:hidden">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate("/deals/bulk-add")}>
                Bulk add
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/archive")}>
                Archived
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <AddDealDialog
        showTrigger={false}
        addButtonClasses=""
        open={addDealOpen}
        onOpenChange={setAddDealOpen}
      />
    </div>
  );
};
